/**
 * Detalle de un expediente (Sprint 5).
 *
 * Orchestrator de las secciones del expediente:
 * - Breadcrumb de retorno a /expedientes.
 * - Header con nombre + status badge + opp linkeada (compone
 *   `EditableTitle`, `EditableDescription`, `BpinSection`,
 *   `ExportPdfButton`).
 * - Checklist con optimistic update + documentos por item.
 * - Controles de estado (forward + reverse).
 * - Panel del Asistente IA (Sprint 17 — gated por plan Pro).
 * - Timeline del status_history.
 *
 * Sprint 18.4.2 (P1-6 audit, mayo 20, 2026): este archivo era de
 * 1282 LOC mezclando 13 subcomponentes con el orchestrator. Ahora
 * cada subcomponente grande vive en `pages/expediente/` y este
 * archivo queda enfocado en componer la página + small helpers.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { ExpedienteAssistantPanel } from "@/components/ExpedienteAssistantPanel";
import { AppliesOpportunities } from "@/components/formulador/AppliesOpportunities";
import { FormulationDocument } from "@/components/formulador/FormulationDocument";
import { OnboardingTour } from "@/components/OnboardingTour";
import { ProjectProgressBar } from "@/components/ProjectProgressBar";
import { ProjectStatusBadge } from "@/components/ProjectStatusBadge";
import { track } from "@/lib/analytics";
import { ApiError, fetchMyPlan, fetchProjectDetail, type ProjectDetailDTO } from "@/lib/api";
import { isIdentificacionConfirmada } from "@/lib/api/formulations";
import { useAuth } from "@/lib/auth";
import { useFormulationByProject } from "@/lib/hooks/useFormulations";
import { useActiveMunicipalityDane } from "@/lib/municipality";

import { etiquetaSector } from "./expediente/_shared";
import { BpinSection } from "./expediente/BpinSection";
import { ChecklistSection } from "./expediente/ChecklistSection";
import { ExpedienteTabs, type TabDef } from "./expediente/ExpedienteTabs";
import { FocusBar } from "./expediente/FocusBar";
import { computeFocusProgress, type ExpedienteTab } from "./expediente/focus";
import { EditableDescription } from "./expediente/EditableDescription";
import { EditableTitle } from "./expediente/EditableTitle";
import { ExportPdfButton } from "./expediente/ExportPdfButton";
import { IntroCard } from "./expediente/IntroCard";
import { StatusControls } from "./expediente/StatusControls";
import { TimelineSection } from "./expediente/TimelineSection";
import { buildExpedienteTourSteps } from "./expediente/tour";
import { CierreConvocatoria } from "./expediente/CierreConvocatoria";

/** Forma de un id de expediente (UUID). */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function ExpedienteDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const activeDane = useActiveMunicipalityDane();
  // Issue #572: un id que no es UUID no existe. Se responde como un 404 sin
  // llamar al API: antes era un 422 reintentado tres veces (~10 s de
  // skeleton) y una tarjeta que hablaba de un formulario.
  const idValido = id !== undefined && UUID_RE.test(id);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["project-detail", id],
    queryFn: () => fetchProjectDetail(id as string, activeDane),
    enabled: idValido,
    retry: (count, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 401 || err.status === 422)
      ) {
        return false;
      }
      return count < 2;
    },
  });

  // Plan summary para decidir si el asistente IA está habilitado.
  // Stale time amplio (5min) — el plan cambia rara vez y revalidar
  // en cada visita al expediente es un round-trip innecesario.
  const planQuery = useQuery({
    queryKey: ["my-plan", activeDane],
    queryFn: () => fetchMyPlan(activeDane as string),
    enabled: Boolean(activeDane),
    staleTime: 5 * 60 * 1000,
  });

  // Sprint 12 · 1: track project_viewed una vez por ID. El effect
  // depende del id de URL para no disparar de más en cada refetch
  // del query.
  useEffect(() => {
    if (id) {
      track({
        event_name: "project_viewed",
        properties: { project_id: id },
      });
    }
  }, [id]);

  // Asistente Formulador (v1.1.0): formulacion activa para expedientes
  // idea-first (opportunity_id === null). El hook debe llamarse
  // incondicionalmente (regla de hooks). `enabled` se evalua en runtime
  // — solo hace fetch cuando el proyecto esta cargado Y es idea-first.
  // data?.opportunity_id === null es seguro: si data es undefined, el
  // campo es undefined, no null, y el hook queda deshabilitado.
  const isIdeaFirst = data?.opportunity_id === null;
  const formulationQuery = useFormulationByProject(id, activeDane, isIdeaFirst && Boolean(data));

  // Contadores de las pestanas. Reusan la logica de la barra de foco para
  // que no puedan decir cosas distintas sobre el mismo avance.
  const avance = data ? computeFocusProgress(data, formulationQuery.data ?? null) : null;
  const formulacionBadge =
    formulationQuery.data && avance?.fuente === "formulacion"
      ? `${avance.hechos}/${avance.total}`
      : undefined;
  const obligatorios = data?.checklist.filter((i) => i.is_required) ?? [];
  const radicarBadge = obligatorios.length
    ? `${obligatorios.filter((i) => i.is_completed).length}/${obligatorios.length}`
    : undefined;

  // Issue #572: sin la formulacion en la mano, la pagina no puede suponer
  // como va un idea-first. Cada caso tiene su cara:
  // - cargando: skeleton. El fallback de "sin formulacion" le decia
  //   "Sigue: Marcar como Radicado" mientras llegaba. `fetchStatus` y no
  //   `isLoading` para cubrir tambien el pedido en pausa por falta de red.
  // - `null` (404): el expediente no tiene formulacion. No hay nada que
  //   formular, asi que se comporta como uno de convocatoria: sin pestaña
  //   Formular y con los controles de estado de siempre.
  // - error (5xx, red): se dice que no cargo y se ofrece reintentar, en vez
  //   del "Completa los 6 modulos" sin pestaña para hacerlo. Reintentar
  //   vuelve a "cargando": sin datos previos, TanStack pasa a `pending`.
  const formulacionCargando =
    isIdeaFirst && formulationQuery.isPending && formulationQuery.fetchStatus !== "idle";
  const sinFormulacion = isIdeaFirst && formulationQuery.data === null;
  const formulacionNoCargo = isIdeaFirst && formulationQuery.isError;
  const reintentarFormulacion = () => void formulationQuery.refetch();

  // Mejora UX (julio 2026): mientras un expediente idea-first no tenga
  // los 6 modulos de identificacion confirmados, no tiene sentido
  // ofrecerle cambio de estado (StatusControls) — no hay nada que
  // radicar todavia. Los expedientes que nacen de una convocatoria
  // (isIdeaFirst=false) nunca tienen formulacion: quedan siempre en
  // `false` y StatusControls se comporta exactamente igual que antes. Un
  // idea-first sin formulacion (404) tampoco tiene modulos que esperar.
  const identificacionPendiente =
    isIdeaFirst &&
    !sinFormulacion &&
    !(formulationQuery.data && isIdentificacionConfirmada(formulationQuery.data));

  // --- Pestanas (paso 3) ---
  //
  // La pestana activa vive en la URL (`?tab=`) y no en useState: recargar
  // no pierde el lugar, el boton "atras" funciona, y se le puede mandar a
  // alguien el link de una pestana puntual.
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs: TabDef[] = [];
  if (isIdeaFirst && (formulationQuery.data || formulacionCargando || formulacionNoCargo)) {
    tabs.push({ id: "formular", label: "Formular", badge: formulacionBadge });
  }
  tabs.push({ id: "radicar", label: "Radicar", badge: radicarBadge });
  if (activeDane) tabs.push({ id: "convocatorias", label: "Convocatorias" });
  tabs.push({ id: "historial", label: "Historial" });

  // Si la URL pide una pestana que este expediente no tiene (ej. `formular`
  // en uno que nacio de convocatoria), caemos a la primera disponible en vez
  // de mostrar un panel vacio.
  const pedida = searchParams.get("tab") as ExpedienteTab | null;
  const activeTab: ExpedienteTab =
    pedida && tabs.some((x) => x.id === pedida) ? pedida : (tabs[0]?.id ?? "radicar");

  function goToTab(tab: ExpedienteTab) {
    // `replace` para no llenar el historial de navegacion con cada clic en
    // una pestana — el boton "atras" debe salir del expediente, no pasear
    // por las pestanas que abriste.
    setSearchParams(
      (prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        return next;
      },
      { replace: true },
    );
  }

  /**
   * La barra de foco manda a un ancla que puede estar en otra pestana. Con
   * pestanas, lo no visible no esta montado: hay que cambiar de pestana y
   * recien despues scrollear, cuando el destino ya existe en el DOM.
   */
  function goToAction(tab: ExpedienteTab, anchorId: string) {
    goToTab(tab);
    requestAnimationFrame(() => {
      const el = document.getElementById(anchorId);
      if (!el) return;
      const quieto = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      el.scrollIntoView({ behavior: quieto ? "auto" : "smooth", block: "center" });
    });
  }

  if (!id) return <ErrorCard detail="ID del expediente faltante." />;
  if (!idValido) return <ErrorCard detail={`No existe expediente con id ${id}.`} />;
  if (isLoading) return <Skeleton />;
  if (isError) {
    const apiError = error instanceof ApiError ? error : null;
    if (apiError?.status === 404) {
      return <ErrorCard detail={`No existe expediente con id ${id}.`} />;
    }
    return <ErrorCard detail={error instanceof Error ? error.message : "Error desconocido"} />;
  }
  if (!data) return null;

  // El tour de un idea-first espera a que la formulacion cargue: su paso
  // "La formulacion" apunta a una seccion que recien existe con ella, y
  // el contador "Paso N de M" se mide al arrancar (#627). Un query
  // deshabilitado (sin municipio activo) no bloquea: isLoading es false.
  const tourReady = !isIdeaFirst || !formulationQuery.isLoading;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {tourReady && <ExpedienteTour isIdeaFirst={isIdeaFirst} />}
      {/*
        Barra de foco (paso 2): en cual expediente estoy, cuanto llevo y que
        me toca ahora. Va antes del breadcrumb porque es lo unico que queda
        fijo al scrollear -- el resto de la pagina pasa por debajo.
      */}
      <FocusBar
        project={data}
        formulation={formulationQuery.data ?? null}
        onNavigate={goToAction}
        cargandoFormulacion={formulacionCargando}
        onReintentarFormulacion={formulacionNoCargo ? reintentarFormulacion : undefined}
        conIA={planQuery.data?.includes_ai_assistant ?? true}
      />
      <Breadcrumb />
      <IntroCard />
      <Header
        project={data}
        onChanged={() => {
          queryClient.invalidateQueries({ queryKey: ["project-detail", id] });
          // Sprint Blindaje — PERF-6 (julio 2026): `Header` compone
          // `BpinSection` — registrar/editar el BPIN puede sacar al
          // proyecto del conteo de "radicados sin BPIN" que alimenta el
          // banner global (AppShell). Invalidamos sin `daneCode` en la
          // key: TanStack matchea por prefijo, así que refresca el
          // banner sin importar cuál sea el muni activo.
          queryClient.invalidateQueries({ queryKey: ["bpin-pending-summary"] });
        }}
      />
      {/*
        Sprint 11: orden secuencial — primero el checklist (lo que el
        funcionario completa dia a dia), despues el control de estado
        (accion puntual cuando termina). El timeline va al final como
        registro historico.
      */}
      {/*
        Paso 3: cuatro zonas de trabajo en vez de nueve secciones apiladas.
        Las pestanas siguen el orden real del trabajo -- primero se formula,
        despues se radica; convocatorias e historial se consultan cuando
        hacen falta. La pestana activa vive en la URL para que recargar no
        pierda el lugar y para poder mandarle un link a alguien.
      */}
      <ExpedienteTabs tabs={tabs} active={activeTab} onChange={goToTab}>
        {activeTab === "formular" && isIdeaFirst && activeDane && formulationQuery.data && (
          <div data-tour-id="formulation-section">
            <FormulationDocument
              formulation={formulationQuery.data}
              municipality={activeDane}
              isPro={planQuery.data?.includes_ai_assistant ?? false}
              projectSector={data.sector}
            />
          </div>
        )}
        {activeTab === "formular" && formulacionCargando && <FormulacionCargando />}
        {activeTab === "formular" && formulacionNoCargo && (
          <FormulacionNoCargo onRetry={reintentarFormulacion} />
        )}

        {activeTab === "radicar" && (
          <div className="space-y-6">
            {/*
              Checklist, cambio de estado y BPIN son tres piezas de la misma
              tarea: dejar el expediente listo para radicar. Separadas, el
              control de estado aparecia antes del trabajo que lo habilita.
            */}
            <ChecklistSection
              project={data}
              onToggled={() => queryClient.invalidateQueries({ queryKey: ["project-detail", id] })}
            />
            {/*
              Sin formulacion cargada no sabemos si ya puede cambiar el
              estado (issue #572): ni los controles ni el "Completa los 6
              modulos" hasta saberlo.
            */}
            {formulacionCargando ? (
              <FormulacionCargando compacto />
            ) : formulacionNoCargo ? (
              <FormulacionNoCargo onRetry={reintentarFormulacion} />
            ) : (
              <StatusControls
                project={data}
                identificacionPendiente={identificacionPendiente}
                onChanged={() => {
                  queryClient.invalidateQueries({ queryKey: ["project-detail", id] });
                  // Sprint Blindaje — PERF-6: un cambio de estado puede meter o
                  // sacar el proyecto del conteo de "radicados sin BPIN".
                  queryClient.invalidateQueries({ queryKey: ["bpin-pending-summary"] });
                }}
              />
            )}
          </div>
        )}

        {activeTab === "convocatorias" && activeDane && (
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <AppliesOpportunities
              municipality={activeDane}
              sector={data.sector}
              excludeOpportunityId={data.opportunity_id}
            />
          </div>
        )}

        {activeTab === "historial" && <TimelineSection history={data.status_history} />}
      </ExpedienteTabs>

      {/*
        Asistente IA (Sprint 17.3). Queda FUERA de las pestanas: se consulta
        desde cualquiera de ellas, y meterlo en una obligaria a salir del
        trabajo para preguntar. Gated por plan Pro + cuota; si planQuery no
        cargo no lo pintamos, para no mostrar "Pro requerido" en falso.
      */}
      {activeDane && planQuery.data && (
        <ExpedienteAssistantPanel
          projectId={id}
          municipality={activeDane}
          hasAssistant={planQuery.data.includes_ai_assistant}
          quota={planQuery.data.includes_ai_assistant ? planQuery.data.ai_messages : null}
          onQuotaUpdated={() => {
            queryClient.invalidateQueries({ queryKey: ["my-plan", activeDane] });
          }}
        />
      )}
    </div>
  );
}

/**
 * Tour guiado de primera visita al detalle de un expediente.
 *
 * Mismo patrón que el del Radar — storageKey por user.id, persistido
 * en localStorage. Si el usuario ya cerró el tour, no vuelve a
 * aparecer. Los pasos contextuales se auto-saltan si su target no
 * está en el DOM (ej. expediente sin checklist). Los pasos y el copy
 * dependen de si el expediente nació de una idea o de una convocatoria
 * (ver `./expediente/tour.ts`).
 */
function ExpedienteTour({ isIdeaFirst }: { isIdeaFirst: boolean }) {
  const { user } = useAuth();
  const steps = useMemo(() => buildExpedienteTourSteps({ isIdeaFirst }), [isIdeaFirst]);
  return (
    <OnboardingTour storageKey={`kaptar.tour.expediente.v1.${user?.id ?? "anon"}`} steps={steps} />
  );
}

function Breadcrumb() {
  return (
    <nav aria-label="Ruta de navegación" className="text-xs">
      <Link
        to="/expedientes"
        className="inline-flex items-center gap-1 text-slate-500 hover:text-brand-700"
      >
        <span aria-hidden="true">&larr;</span>
        Volver a expedientes
      </Link>
    </nav>
  );
}

function Header({ project, onChanged }: { project: ProjectDetailDTO; onChanged: () => void }) {
  return (
    <header className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <ProjectStatusBadge status={project.status} />
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold tracking-wide text-slate-700 uppercase">
            {etiquetaSector(project.sector)}
          </span>
        </div>
        <ExportPdfButton project={project} />
      </div>
      <EditableTitle project={project} onChanged={onChanged} />
      {project.opportunity_title && (
        <p className="mt-1 text-sm text-slate-600">
          Convocatoria: <strong>{project.opportunity_title}</strong>
          <CierreConvocatoria
            closesAt={project.opportunity_closes_at}
            untilQuotaExhausted={project.opportunity_until_quota_exhausted}
          />
        </p>
      )}
      <ProjectProgressBar checklist={project.checklist} />
      <EditableDescription project={project} onChanged={onChanged} />
      <BpinSection project={project} onChanged={onChanged} />
    </header>
  );
}

function Skeleton() {
  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="h-4 w-40 animate-pulse rounded bg-slate-100" />
      <div className="h-24 animate-pulse rounded-2xl bg-slate-100" />
      <div className="h-40 animate-pulse rounded-2xl bg-slate-100" />
      <div className="h-48 animate-pulse rounded-2xl bg-slate-100" />
    </div>
  );
}

/**
 * La formulacion de un idea-first no cargo (5xx o red). Dice que paso y
 * ofrece reintentar, en vez de dejar al usuario frente a un "Completa los 6
 * modulos" sin forma de hacerlo (issue #572).
 */
function FormulacionNoCargo({ onRetry }: { onRetry: () => void }) {
  return (
    <section
      role="alert"
      data-testid="formulacion-no-cargo"
      className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900"
    >
      <p className="font-semibold">No pudimos cargar la formulación de este expediente.</p>
      <p className="mt-1 text-amber-800">
        Hasta que cargue no sabemos si ya puedes cambiar el estado. Revisa tu conexión y vuelve a
        intentarlo.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 min-h-[36px] rounded-lg border border-amber-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:outline-hidden"
      >
        Reintentar
      </button>
    </section>
  );
}

/**
 * Lugar reservado mientras carga la formulacion de un idea-first (issue
 * #572). `compacto` ocupa el lugar del control de estado en Radicar; sin
 * el, el del documento en Formular.
 */
function FormulacionCargando({ compacto = false }: { compacto?: boolean }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid={compacto ? "status-controls-cargando" : "formulacion-cargando"}
      className="space-y-4 motion-safe:animate-pulse"
    >
      <span className="sr-only">Cargando la formulación…</span>
      {compacto ? (
        <div aria-hidden="true" className="h-32 rounded-2xl bg-slate-100" />
      ) : (
        <>
          <div aria-hidden="true" className="h-24 rounded-2xl bg-slate-100" />
          <div aria-hidden="true" className="h-40 rounded-2xl bg-slate-100" />
          <div aria-hidden="true" className="h-40 rounded-2xl bg-slate-100" />
        </>
      )}
    </div>
  );
}

function ErrorCard({ detail }: { detail: string }) {
  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <Breadcrumb />
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
        <h1 className="text-lg font-bold text-red-900">Error</h1>
        <p className="mt-2 text-sm text-red-800">{detail}</p>
      </div>
    </div>
  );
}
